import { Box, Typography } from '@mui/material';
import { NotificationType } from '@okletsplay/core';
import { Route } from 'app/enums';
import { EntityStoreName } from 'app/enums/store';
import { useEntityRepositoryStatus } from 'app/hooks/store';
import { NotificationEntity } from 'app/interfaces/entity';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { useMatchModal } from 'modules/matches/hooks';
import { MatchNotificationTile } from 'modules/notifications/components/MatchNotificationTile';
import { TournamentNotificationTile } from 'modules/notifications/components/TournamentNotificationTile';
import { BALANCE_NOTIFICATION_TYPES } from 'modules/notifications/constants';
import { NotificationsStoreStatus } from 'modules/notifications/store/notifications/enums';
import {
  markAllAsRead,
  updateNotification,
} from 'modules/notifications/store/notifications/repository';
import { notificationsSelector } from 'modules/notifications/store/notifications/selectors';
import { useTournamentModal, useTournamentPlatformType } from 'modules/tournaments/hooks';
import React, { FC, useCallback, useRef } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { EmptyPlaceholder } from 'shared/components/EmptyPlaceholder';
import { Loader } from 'shared/components/Loader';
import { BalanceNotificationTile } from '../BalanceNotificationTile';
import { useStyles } from './NotificationsList.styles';

interface NotificationsListProps {
  loading: boolean;
  onNext: () => void;
}

export const NotificationsList: FC<NotificationsListProps> = ({ loading, onNext = () => null }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { classes } = useStyles();
  const ref = useRef<HTMLDivElement>();
  const updating = useEntityRepositoryStatus({
    [EntityStoreName.Notifications]: NotificationsStoreStatus.MarkAllAsRead,
  });

  const data = useSelector(notificationsSelector.all);
  const total = useSelector(notificationsSelector.total);
  const offset = useSelector(notificationsSelector.offset);

  const { setMatchIdQueryParam } = useMatchModal();
  const { setTournamentIdQueryParam } = useTournamentModal();
  const { checkIsKnockoutEnabled } = useTournamentPlatformType();

  const clickItemHandler = useCallback(
    (notification: NotificationEntity) => {
      if (!notification?.seen) {
        dispatch(updateNotification(notification?.id));
      }

      if (notification?.match) {
        setMatchIdQueryParam(notification?.match?.id ?? '');
      }

      if (!notification.tournament?.game?.platformType) {
        return;
      }

      if (notification?.tournament && notification?.tournament?.id) {
        const isKnockoutTournament = checkIsKnockoutEnabled(
          notification.tournament.game?.platformType
        );

        if (isKnockoutTournament) {
          navigate(Route.TournamentOverview.replace(':id', notification?.tournament?.id ?? ''));
          return;
        }

        setTournamentIdQueryParam(notification?.tournament?.id ?? '');
      }
      if (notification?.type === NotificationType.BalanceAutoSwapSuccessful) {
        navigate(Route.PaymentTransactions);
      }
    },
    [checkIsKnockoutEnabled, dispatch, navigate, setMatchIdQueryParam, setTournamentIdQueryParam]
  );

  const markAllAsReadHandler = useCallback(() => {
    if (!updating) {
      dispatch(markAllAsRead());
    }
  }, [dispatch, updating]);

  if (isEmpty(data) && !loading) {
    return (
      <Box className={classes.emptyRoot}>
        <EmptyPlaceholder text="No notifications." size={100} />
      </Box>
    );
  }

  if (loading && !offset) {
    return <Loader size={100} />;
  }

  return (
    <Box ref={ref} className={classes.root}>
      <Box className={classes.markAllAsRead}>
        <Typography
          component="span"
          variant="subtitle2"
          onClick={markAllAsReadHandler}
          className={classNames({ disabled: updating })}
        >
          Mark all as read
        </Typography>
      </Box>
      <InfiniteScroll
        scrollThreshold={0.95}
        height="100%"
        loader={
          <Box>
            <Loader size={30} />
          </Box>
        }
        dataLength={data?.length}
        hasMore={data?.length !== total}
        next={onNext}
      >
        {data.map((notification) => (
          <Box key={notification.id}>
            {notification.match && (
              <MatchNotificationTile notification={notification} onClick={clickItemHandler} />
            )}
            {notification.tournament && (
              <TournamentNotificationTile notification={notification} onClick={clickItemHandler} />
            )}
            {BALANCE_NOTIFICATION_TYPES.includes(notification.type) ? (
              <BalanceNotificationTile notification={notification} onClick={clickItemHandler} />
            ) : null}
          </Box>
        ))}
      </InfiniteScroll>
    </Box>
  );
};
